import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { Trophy, Crown, Medal, TrendUp, Flame } from "@phosphor-icons/react";

const PERIODS = [
  { v: "week", l: "Semana" },
  { v: "month", l: "Mês" },
  { v: "year", l: "Ano" },
  { v: "all", l: "Todo o Tempo" },
  { v: "streak", l: "Login Seguido" },
];

export default function Ranking() {
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    const isStreak = tab === "streak";
    Promise.all([
      api.get("/ranking", { params: { period: tab, limit: 50 } }).then(r => r.data),
      isStreak ? Promise.resolve([]) : api.get("/manga/popular", { params: { period: tab, limit: 12 } }).then(r => r.data),
    ]).then(([rk, pop]) => {
      if (ignore) return;
      setRows(rk);
      setPopular(pop);
    }).finally(() => !ignore && setLoading(false));
    return () => { ignore = true; };
  }, [tab]);

  return (
    <div className="pt-20 max-w-6xl mx-auto px-5 lg:px-8 pb-24" data-testid="ranking-page">
      <div className="mb-8">
        <div className="font-mono-accent text-xs text-[#E63946] mb-2 flex items-center gap-2">
          <Trophy size={14} weight="fill" /> RANKING
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">{tab === "streak" ? "Ranking de Login" : "Ranking de Pontos"}</h1>
        <p className="text-zinc-400 mt-2">{tab === "streak" ? "Os membros com mais dias de login seguidos. Faz login todos os dias para manter a tua streak!" : "Os membros e mangás mais ativos da comunidade PortugalManga."}</p>
      </div>

      {/* Period tabs */}
      <div className="inline-flex bg-[#121217] border border-white/5 rounded-xl p-1 mb-8" data-testid="period-tabs">
        {PERIODS.map(p => (
          <button
            key={p.v}
            onClick={() => setTab(p.v)}
            data-testid={`period-${p.v}`}
            className={`px-4 py-2 rounded-lg text-sm font-display font-semibold transition-all ${
              tab === p.v ? "bg-[#E63946] text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            {p.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>
      ) : (
        <div className={tab === "streak" ? "" : "grid lg:grid-cols-[1fr_1fr] gap-8"}>
          {/* Points ranking */}
          <div>
            <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
              {tab === "streak" ? (
                <><Flame size={20} weight="fill" className="text-orange-400" /> Top Login Seguido</>
              ) : (
                <><Crown size={20} weight="fill" className="text-[#F59E0B]" /> Top Membros</>
              )}
            </h2>
            <div className="space-y-2" data-testid="ranking-users">
              {rows.length === 0 && (
                <div className="border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 text-sm">
                  {tab === "streak" ? "Ainda ninguém tem streak. Faz login todos os dias para começar!" : "Sem atividade neste período ainda."}
                </div>
              )}
              {rows.map((u, i) => (
                <Link
                  key={u.user_id}
                  to={`/membros/${u.slug || u.user_id}`}
                  data-testid={`ranking-row-${i + 1}`}
                  className={`flex items-center gap-4 rounded-xl p-3 pr-5 transition-all hover:border-[#E63946]/30 ${
                    i < 3
                      ? "bg-gradient-to-r from-[#1a0810] to-[#0f0f14] border border-[#E63946]/20"
                      : "bg-[#0f0f14] border border-white/5"
                  }`}
                >
                  <div className={`w-10 text-center font-display font-black text-xl ${
                    i === 0 ? "text-[#FFD700]" : i === 1 ? "text-[#C0C0C0]" : i === 2 ? "text-[#CD7F32]" : "text-zinc-500"
                  }`}>
                    {i < 3 ? <Medal size={26} weight="fill" className="inline" /> : `#${i + 1}`}
                  </div>
                  {u.picture ? (
                    <img src={resolveImageUrl(u.picture)} alt="" className="w-11 h-11 rounded-full object-cover border border-white/10" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-sm font-display font-bold">
                      {(u.name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold truncate">{u.name || "Sem nome"}</div>
                    <div className="text-[11px] text-zinc-500 font-mono-accent">NÍVEL {u.level}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {tab === "streak" ? (
                      <>
                        <div className="font-display text-xl font-black text-orange-400 flex items-center gap-1 justify-end">
                          <Flame size={18} weight="fill" /> {u.period_xp}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono-accent uppercase">DIAS</div>
                      </>
                    ) : (
                      <>
                        <div className="font-display text-xl font-black text-[#E63946]">{u.period_xp}</div>
                        <div className="text-[10px] text-zinc-500 font-mono-accent uppercase">XP</div>
                      </>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Popular manga (hidden on streak tab) */}
          {tab !== "streak" && (
            <div>
              <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
                <TrendUp size={20} weight="fill" className="text-[#7209B7]" /> Mangás Populares
              </h2>
              <div className="grid grid-cols-2 gap-3" data-testid="ranking-popular-manga">
                {popular.length === 0 && (
                  <div className="col-span-2 border border-dashed border-white/10 rounded-xl p-8 text-center text-zinc-500 text-sm">
                    Ainda sem visualizações neste período.
                  </div>
                )}
                {popular.map((m, i) => (
                  <Link key={m.manga_id} to={`/manga/${m.slug || m.manga_id}`} className="flex items-center gap-3 bg-[#0f0f14] border border-white/5 rounded-xl p-2 pr-4 hover:border-[#7209B7]/40 transition-all" data-testid={`popular-manga-${i + 1}`}>
                    <div className="w-8 text-center font-display font-black text-lg text-zinc-500">#{i + 1}</div>
                    {m.cover_url ? (
                      <img src={m.cover_url} alt={m.title} className="w-12 h-16 rounded object-cover" />
                    ) : (
                      <div className="w-12 h-16 bg-[#1C1C22] rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-sm line-clamp-2">{m.title}</div>
                      {m.author && <div className="text-[10px] text-zinc-500 truncate">{m.author}</div>}
                      <div className="text-[10px] text-[#7209B7] font-mono-accent mt-1">
                        {tab === "all" ? `${m.view_count || 0} views` : `${m.period_views || 0} views`}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
